import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassPageLayout - Main application layout with gradient background
 *
 * A wrapper component that provides the glass morphism page background
 * with configurable gradient colors and layout structure.
 *
 * @example
 * ```tsx
 * <GlassPageLayout>
 *   <GlassNavigation />
 *   <main className="flex">
 *     <GlassSidebar />
 *     <div className="flex-1">
 *       {children}
 *     </div>
 *   </main>
 * </GlassPageLayout>
 * ```
 */

export interface GlassPageLayoutProps {
  /** Page content */
  children: React.ReactNode;
  /** Background gradient variant */
  variant?: 'default' | 'blue' | 'purple' | 'green' | 'minimal';
  /** Show background patterns/shapes */
  showPatterns?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const gradientVariants = {
  default: cn(
    'bg-gradient-to-br',
    'from-slate-50 via-slate-100 to-slate-200',
    'dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
  ),
  blue: cn(
    'bg-gradient-to-br',
    'from-blue-50 via-slate-50 to-indigo-50',
    'dark:from-slate-900 dark:via-blue-950 dark:to-slate-900'
  ),
  purple: cn(
    'bg-gradient-to-br',
    'from-blue-50 via-slate-50 to-sky-50',
    'dark:from-slate-900 dark:via-blue-950 dark:to-slate-900'
  ),
  green: cn(
    'bg-gradient-to-br',
    'from-emerald-50 via-slate-50 to-teal-50',
    'dark:from-slate-900 dark:via-emerald-950 dark:to-slate-900'
  ),
  minimal: cn('bg-slate-50 dark:bg-slate-900'),
};

export const GlassPageLayout: React.FC<GlassPageLayoutProps> = ({
  children,
  variant = 'default',
  showPatterns = true,
  className,
}) => {
  return (
    <div
      className={cn(
        'min-h-screen relative overflow-x-hidden',
        gradientVariants[variant],
        className
      )}
    >
      {/* Background patterns */}
      {showPatterns && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {/* Large gradient orbs */}
          <div
            className="absolute -top-[40%] -left-[20%] w-[60%] h-[80%] rounded-full opacity-30 dark:opacity-20"
            style={{
              background: 'radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute -bottom-[30%] -right-[10%] w-[50%] h-[70%] rounded-full opacity-30 dark:opacity-20"
            style={{
              background: 'radial-gradient(ellipse, rgba(139, 92, 246, 0.12) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute top-[40%] left-[30%] w-[40%] h-[50%] rounded-full opacity-20 dark:opacity-15"
            style={{
              background: 'radial-gradient(ellipse, rgba(16, 185, 129, 0.08) 0%, transparent 60%)',
            }}
          />

          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />
        </div>
      )}

      {/* Page content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

GlassPageLayout.displayName = 'GlassPageLayout';

/**
 * GlassPageContainer - Content wrapper with max-width and padding
 */
export interface GlassPageContainerProps {
  /** Container content */
  children: React.ReactNode;
  /** Maximum width */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | '7xl' | 'full';
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Center content */
  centered?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

const paddingClasses = {
  none: '',
  sm: 'px-4 py-4',
  md: 'px-6 py-6',
  lg: 'px-8 py-8',
};

export const GlassPageContainer: React.FC<GlassPageContainerProps> = ({
  children,
  maxWidth = '7xl',
  padding = 'md',
  centered = true,
  className,
}) => {
  return (
    <div
      className={cn(
        'w-full',
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        centered && 'mx-auto',
        className
      )}
    >
      {children}
    </div>
  );
};

GlassPageContainer.displayName = 'GlassPageContainer';
